import { useMemo } from 'react';
import { TEST_IDS_ENABLED } from '../constants';

const EMPTY: TestIdProps = Object.freeze({});
const DEFAULT_SEPARATOR = '-';
const SEPARATORS = new Set(['_', '.', '-']);

/** The object spread into a JSX element to attach a `data-testid` attribute. */
export type TestIdProps = { 'data-testid'?: string };

/** A single argument accepted by {@link getTestId} / {@link useTestId}. */
export type TestIdPart = string | number | null | undefined;

/**
 * Hook variant of {@link getTestId}. Memoizes the returned object on the
 * joined value, so the same reference is returned across renders when the
 * inputs are unchanged — useful when spreading into memoized children.
 *
 * Subject to the same `VITE_ENABLE_TEST_IDS` gating, separator-override,
 * and short-circuit rules as `getTestId`.
 *
 * Must be called at the top level of a component or another hook (rules
 * of hooks). For uses inside `.map()` or other non-hook contexts, use
 * {@link getTestId}.
 *
 * @example
 *   const testIdProps = useTestId(id);
 *   return <Input id={id} {...testIdProps} />;
 */
export default function useTestId(...parts: TestIdPart[]): TestIdProps {
  const joined = joinParts(parts);
  return useMemo(
    () => (TEST_IDS_ENABLED && joined != null ? { 'data-testid': joined } : EMPTY),
    [joined]
  );
}

/**
 * Builds a `{ 'data-testid': ... }` spread object, gated by the
 * `VITE_ENABLE_TEST_IDS` env var. Returns a frozen empty object when the
 * env var is off so the spread becomes a no-op.
 *
 * Parts are joined with `-` by default. Passing one of `'_'`, `'.'`, or
 * `'-'` as an argument switches the separator for all subsequent parts,
 * which lets you mix conventions like `<id>_<field>-<action>`.
 *
 * If any non-separator part is `null`, `undefined`, or `''`, the call
 * short-circuits and returns the empty object — so callers don't need
 * to guard against missing ids at the call site.
 *
 * Use this variant inside loops, callbacks, or anywhere hooks can't run.
 * For top-level component bodies prefer {@link useTestId}, which
 * additionally memoizes the result for referential stability.
 *
 * @example
 *   getTestId('root', 'email')        // { 'data-testid': 'root-email' }
 *   getTestId('root', '_', 'email')   // { 'data-testid': 'root_email' }
 *   getTestId(undefined, 'x')         // {}  (short-circuited)
 */
export function getTestId(...parts: TestIdPart[]): TestIdProps {
  if (!TEST_IDS_ENABLED) return EMPTY;
  const joined = joinParts(parts);
  return joined == null ? EMPTY : { 'data-testid': joined };
}

/**
 * Joins parts into a test-id string, honoring inline separator-override
 * markers. Returns `null` if no real content was emitted (empty input or
 * any non-separator part was nullish/empty).
 */
function joinParts(parts: TestIdPart[]): string | null {
  let sep = DEFAULT_SEPARATOR;
  let result = '';
  let hasContent = false;
  for (const p of parts) {
    if (typeof p === 'string' && SEPARATORS.has(p)) {
      sep = p;
      continue;
    }
    if (p == null || p === '') return null;
    if (hasContent) {
      result += sep + String(p);
    } else {
      result = String(p);
      hasContent = true;
    }
  }
  return hasContent ? result : null;
}
